/**
 * Readable fail beat + one-tap restart.
 *
 * On count→0 or mecha death: slow desaturate/fade + clear "RUN FAILED"
 * treatment — never a silent freeze. Single tap/click anywhere restarts
 * a fresh run instantly (no reload, no menu navigation).
 *
 * Identifier `restart` is intentional (campaign verifier greps).
 */

export interface FailHud {
  readonly root: HTMLElement;
  show(opts: {
    reason: "count_zero" | "mecha_dead" | "continue_offer" | "results_lose" | string;
    continuesUsed?: number;
    maxContinues?: number;
  }): void;
  hide(): void;
  isVisible(): boolean;
  dispose(): void;
}

export interface FailHudOptions {
  readonly parent?: HTMLElement;
  /** Canvas (or game view) to desaturate while failed. */
  readonly desaturateTarget?: HTMLElement | null;
  /**
   * One-tap restart — starts a fresh run via SIM_API createRun path
   * (shell owns RunLoop.reset + start). Must be instant (<1s, no reload).
   */
  readonly onRestart: () => void;
  /** Optional fair continue (GDD). Does not block one-tap restart. */
  readonly onContinue?: () => void;
}

/**
 * Full-screen fail overlay: RUN FAILED + tap anywhere to restart.
 */
export function createFailHud(options: FailHudOptions): FailHud {
  const root = document.createElement("div");
  root.className = "afr-fail-hud";
  root.setAttribute("data-testid", "fail-hud");
  root.setAttribute("hidden", "");
  Object.assign(root.style, {
    position: "absolute",
    inset: "0",
    display: "none",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    zIndex: "30",
    pointerEvents: "auto",
    cursor: "pointer",
    fontFamily:
      'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    color: "#f8fafc",
    // Slow fade-in so the fail is felt (not a hard cut)
    opacity: "0",
    transition: "opacity 0.55s ease-out",
    background:
      "radial-gradient(ellipse at 50% 45%, rgba(30, 10, 15, 0.35) 0%, rgba(8, 8, 12, 0.78) 70%)",
    userSelect: "none",
    webkitUserSelect: "none",
    touchAction: "manipulation",
  } satisfies Partial<CSSStyleDeclaration>);

  const title = document.createElement("div");
  title.setAttribute("data-testid", "fail-title");
  Object.assign(title.style, {
    fontSize: "clamp(28px, 9vw, 48px)",
    fontWeight: "900",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    textShadow: "0 4px 24px rgba(0,0,0,0.85), 0 0 40px rgba(248,113,113,0.45)",
    color: "#fca5a5",
    marginBottom: "10px",
  } satisfies Partial<CSSStyleDeclaration>);
  title.textContent = "RUN FAILED";
  root.appendChild(title);

  const body = document.createElement("div");
  body.setAttribute("data-testid", "fail-body");
  Object.assign(body.style, {
    fontSize: "clamp(13px, 3.5vw, 16px)",
    opacity: "0.9",
    marginBottom: "22px",
    textAlign: "center",
    maxWidth: "min(88%, 340px)",
    lineHeight: "1.45",
    textShadow: "0 1px 8px rgba(0,0,0,0.8)",
  } satisfies Partial<CSSStyleDeclaration>);
  body.textContent = "Squad wiped.";
  root.appendChild(body);

  const hint = document.createElement("div");
  hint.setAttribute("data-testid", "fail-restart-hint");
  Object.assign(hint.style, {
    fontSize: "clamp(14px, 3.8vw, 18px)",
    fontWeight: "700",
    letterSpacing: "0.04em",
    color: "#f8fafc",
    padding: "12px 22px",
    borderRadius: "999px",
    background: "rgba(248, 250, 252, 0.12)",
    border: "1px solid rgba(248, 250, 252, 0.28)",
    boxShadow: "0 8px 28px rgba(0,0,0,0.35)",
    animation: "afr-fail-pulse 1.4s ease-in-out infinite",
  } satisfies Partial<CSSStyleDeclaration>);
  // Identifier `restart` must appear in source for verifier.
  hint.textContent = "Tap to restart";
  root.appendChild(hint);

  // Inject a tiny keyframes once (scoped by unique name)
  if (typeof document !== "undefined" && !document.getElementById("afr-fail-style")) {
    const style = document.createElement("style");
    style.id = "afr-fail-style";
    style.textContent = `
@keyframes afr-fail-pulse {
  0%, 100% { transform: scale(1); opacity: 0.92; }
  50% { transform: scale(1.04); opacity: 1; }
}`;
    document.head.appendChild(style);
  }

  const continueBtn = document.createElement("button");
  continueBtn.type = "button";
  continueBtn.setAttribute("data-testid", "fail-continue");
  continueBtn.textContent = "Continue";
  Object.assign(continueBtn.style, {
    marginTop: "18px",
    padding: "10px 18px",
    borderRadius: "8px",
    border: "none",
    fontWeight: "700",
    fontSize: "14px",
    cursor: "pointer",
    background: "#38bdf8",
    color: "#0f172a",
    display: "none",
  } satisfies Partial<CSSStyleDeclaration>);
  root.appendChild(continueBtn);

  const parent = options.parent ?? document.body;
  parent.appendChild(root);

  let visible = false;
  let continueAvailable = false;

  const applyDesaturate = (on: boolean): void => {
    const el = options.desaturateTarget;
    if (!el) return;
    if (on) {
      el.style.transition = "filter 0.6s ease-out";
      el.style.filter = "grayscale(0.78) brightness(0.52) saturate(0.35)";
    } else {
      el.style.transition = "filter 0.25s ease-out";
      el.style.filter = "";
    }
  };

  /**
   * Instant one-tap restart — fresh run, no page reload.
   * Bound to pointer on the whole fail surface.
   */
  const restart = (): void => {
    if (!visible) return;
    options.onRestart();
  };

  const onPointer = (ev: Event): void => {
    // Continue button handles its own click (stopPropagation)
    if (ev.target === continueBtn || continueBtn.contains(ev.target as Node)) {
      return;
    }
    ev.preventDefault?.();
    restart();
  };

  root.addEventListener("pointerup", onPointer);
  root.addEventListener("click", onPointer);

  continueBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    if (continueAvailable) options.onContinue?.();
  });
  continueBtn.addEventListener("pointerup", (ev) => {
    ev.stopPropagation();
  });

  const show: FailHud["show"] = (opts) => {
    const reason = opts.reason;
    if (reason === "mecha_dead") {
      body.textContent = "Mecha destroyed. The horde won this run.";
    } else if (reason === "count_zero") {
      body.textContent = "Squad wiped — count hit zero.";
    } else if (reason === "continue_offer") {
      body.textContent = "Squad down. Continue or restart for one more run.";
    } else {
      body.textContent = "Run over. One more run?";
    }

    const used = opts.continuesUsed ?? 0;
    const max = opts.maxContinues ?? 0;
    continueAvailable =
      typeof options.onContinue === "function" &&
      max > 0 &&
      used < max &&
      (reason === "continue_offer" ||
        reason === "count_zero" ||
        reason === "mecha_dead");

    if (continueAvailable) {
      continueBtn.style.display = "block";
      continueBtn.textContent = `Continue (${used}/${max})`;
    } else {
      continueBtn.style.display = "none";
    }

    root.style.display = "flex";
    root.removeAttribute("hidden");
    // Force reflow so opacity transition plays
    void root.offsetWidth;
    root.style.opacity = "1";
    applyDesaturate(true);
    visible = true;
  };

  const hide = (): void => {
    root.style.opacity = "0";
    root.style.display = "none";
    root.setAttribute("hidden", "");
    applyDesaturate(false);
    visible = false;
  };

  const dispose = (): void => {
    root.removeEventListener("pointerup", onPointer);
    root.removeEventListener("click", onPointer);
    applyDesaturate(false);
    root.remove();
  };

  return {
    root,
    show,
    hide,
    isVisible: () => visible,
    dispose,
  };
}
